import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import * as api from './api.ts';
import { OpenDashboard, Page } from './invitation.tsx';
import { SignIn } from './screens.tsx';
import { ReverifyModal } from './reverify.tsx';
import { Button, Finding, Mono, Spinner } from './ui.tsx';

/**
 * The organisation join link page (`/join/<token>`, migration 0142).
 *
 * An admin posts one link into a Slack or Teams channel; whoever opens it
 * signs in and joins as a member (the `occupant` role) — alerted, able to
 * answer that they are safe, and nothing more. The link can leak, so it grants
 * no access to the roster or to raising an alert; an administrator promotes
 * people afterwards from People. The page names the organisation and nothing
 * else, and an unknown, expired or withdrawn link reads the same.
 */

export function parseJoinPath(pathname: string): { token: string } | null {
  const m = /^\/join\/([^/]+)\/?$/.exec(pathname);
  if (!m) return null;
  return { token: decodeURIComponent(m[1]!) };
}

export function JoinPage({
  token,
  signedIn,
  onSignedIn,
}: {
  token: string;
  signedIn: boolean;
  onSignedIn: () => void;
}) {
  const qc = useQueryClient();
  const [wantsToJoin, setWantsToJoin] = useState(false);

  const link = useQuery({
    queryKey: ['join-link', token],
    queryFn: () => api.getJoinLink(token),
    retry: false,
  });

  const joined = useMutation({
    mutationFn: () => api.acceptJoinLink(token),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['me'] }),
  });

  if (link.isPending) return <Spinner />;

  if (link.isError || !link.data) {
    const gone = link.error instanceof api.ApiError && link.error.status === 404;
    return (
      <Page title={gone ? 'This join link is no longer valid' : 'The join link could not be read'}>
        <p className="mb-6 text-[17px] leading-[23px] text-[var(--ink-2)]">
          {gone
            ? 'It may have expired or been replaced with a new one. Ask whoever shared it for the current link.'
            : 'This is a connection problem, not a problem with the link. Try again in a moment.'}
        </p>
        {gone ? null : (
          <Button variant="primary" size="auth" full onClick={() => void link.refetch()}>
            Try again
          </Button>
        )}
      </Page>
    );
  }

  const organisation = link.data.organisation;

  if (joined.isSuccess && joined.data) {
    return (
      <Page
        title={
          joined.data.joined ? `You've joined ${organisation}` : `You're already in ${organisation}`
        }
      >
        <p className="mb-6 text-[17px] leading-[23px] text-[var(--ink-2)]">
          {joined.data.joined
            ? 'You will be alerted if they run a drill or declare an emergency, and can answer that you are safe.'
            : 'Nothing has changed about your place in this organisation.'}
        </p>
        <OpenDashboard />
      </Page>
    );
  }

  // Signed out and choosing to join: sign in in place. The URL stays on this
  // page, so signing in lands straight back here.
  if (wantsToJoin && !signedIn) {
    return <SignIn onSignedIn={onSignedIn} />;
  }

  return (
    <Page title={`Join ${organisation}`}>
      <p className="mb-6 text-[17px] leading-[23px] text-[var(--ink-2)]">
        You would be alerted if {organisation} runs a drill or declares an emergency, and could
        answer that you are safe. An administrator can give you more access later.
      </p>
      {signedIn ? (
        <div className="space-y-3">
          <Button
            variant="primary"
            size="auth"
            full
            disabled={joined.isPending}
            onClick={() => joined.mutate()}
          >
            {joined.isPending ? 'Joining…' : `Join ${organisation}`}
          </Button>
          {joined.isError ? <Finding>{api.errorMessage(joined.error)}</Finding> : null}
        </div>
      ) : (
        <Button variant="primary" size="auth" full onClick={() => setWantsToJoin(true)}>
          Sign in to join
        </Button>
      )}
    </Page>
  );
}

/**
 * The admin's side of the join link, on People: make one, copy it, replace it,
 * withdraw it. The URL is shown only in the answer to making it — the server
 * stores a hash, so a lost link is replaced, never recovered. Every joiner is
 * an occupant, and the panel says so before anybody posts it anywhere.
 */
export function JoinLinkPanel({ orgId }: { orgId: string }) {
  const qc = useQueryClient();
  const me = useQuery({ queryKey: ['me'], queryFn: api.getMe });
  const status = useQuery({
    queryKey: ['join-link-status', orgId],
    queryFn: () => api.getJoinLinkStatus(orgId),
  });
  const [minted, setMinted] = useState<{ url: string; expiresAt: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const [reverify, setReverify] = useState<null | (() => void)>(null);

  const stale = (e: Error, retry: () => void) => {
    if (e instanceof api.ApiError && e.detail.code === 'stale_session') setReverify(() => retry);
  };

  const mint = useMutation({
    mutationFn: () => api.mintJoinLink(orgId),
    onSuccess: (res) => {
      if (res) setMinted(res);
      setCopied(false);
      void qc.invalidateQueries({ queryKey: ['join-link-status', orgId] });
    },
    onError: (e) => stale(e, () => mint.mutate()),
  });
  const revoke = useMutation({
    mutationFn: () => api.revokeJoinLink(orgId),
    onSuccess: () => {
      setMinted(null);
      void qc.invalidateQueries({ queryKey: ['join-link-status', orgId] });
    },
    onError: (e) => stale(e, () => revoke.mutate()),
  });

  const live = status.data?.active ?? false;
  const until = (iso: string | null | undefined) =>
    iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'long' }) : null;
  const failure = [mint, revoke].find(
    (m) =>
      m.isError && !(m.error instanceof api.ApiError && m.error.detail.code === 'stale_session'),
  );

  return (
    <div className="space-y-4">
      {reverify && me.data?.email ? (
        <ReverifyModal
          email={me.data.email}
          onClose={() => setReverify(null)}
          onVerified={() => {
            const retry = reverify;
            setReverify(null);
            retry();
          }}
        />
      ) : null}
      <p className="max-w-prose text-[14px] text-[var(--ink-2)]">
        One link for the whole workplace, to post in Slack, Teams or an all-staff email. Anyone who
        opens it and signs in joins as an <strong>occupant</strong>: they are alerted and can answer
        that they are safe, and nothing more — they cannot send an alert or see who else is here.
        Make someone staff or an administrator from their row afterwards. The link lasts 14 days.
      </p>

      {minted ? (
        <div className="space-y-2">
          <Mono>{minted.url}</Mono>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="primary"
              onClick={() => {
                void navigator.clipboard?.writeText(minted.url).then(
                  () => setCopied(true),
                  () => setCopied(false),
                );
              }}
            >
              {copied ? 'Copied' : 'Copy link'}
            </Button>
          </div>
          <p className="text-[13px] text-[var(--ink-2)]">
            Works until {until(minted.expiresAt)}. Copy it now — it is not shown again.
          </p>
        </div>
      ) : live ? (
        <p className="text-[14px] text-[var(--ink)]">
          A link is live until {until(status.data?.expiresAt)}. {status.data?.joinedCount ?? 0}{' '}
          joined through it so far. It is not shown again once made; to share it again, make a new
          one — the old one stops working.
        </p>
      ) : status.data ? (
        <p className="text-[14px] text-[var(--ink-2)]">No link is live.</p>
      ) : null}

      <div className="flex flex-wrap gap-2">
        <Button
          variant={minted ? 'secondary' : 'primary'}
          disabled={mint.isPending}
          onClick={() => mint.mutate()}
        >
          {mint.isPending ? 'Making a link…' : live || minted ? 'Make a new link' : 'Make a link'}
        </Button>
        {live || minted ? (
          <Button variant="quiet" disabled={revoke.isPending} onClick={() => revoke.mutate()}>
            {revoke.isPending ? 'Withdrawing…' : 'Withdraw the link'}
          </Button>
        ) : null}
      </div>
      {live || minted ? (
        <p className="text-[13px] text-[var(--ink-2)]">
          Making a new link stops the old one working. People who already joined stay joined.
        </p>
      ) : null}
      {failure ? <Finding>{api.errorMessage(failure.error)}</Finding> : null}
    </div>
  );
}
